import { useState, useEffect } from "react";
import {
  Avatar,
  Button,
  Input,
  Form,
  Select,
  Row,
  Col,
  Space,
  Spin,
  Tooltip,
  message,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import UserIcon from "../../../../assets/images/icons/userLogo.svg";
import { useLocation, useParams } from "react-router-dom";
import { useStore } from "../../../../store";
import { TAB_KEYS } from "../../../../config";
import { useTab } from "../../../../custom-hooks/useTab";
import { useConnectsuForm } from "../../../../custom-hooks/useConnectsuForm";
import { getFormFieldValue } from "../../../../utils";
import ImageLabel from "../../../image-label";
import ImageSlider from "../../../slider/ImageSlider";
import userServiceApi from "../../../../api/userServiceApi";
import reArrangeOrder from "../../../common/DragAndDropList/rearrangeHelper";
import {
  DragAndDropList,
  GifCreator,
  GifResultPreview,
  createEmptyGifState,
  hydrateGifCreatorState,
} from "../../../common";
import './carousel.scss';
import { sortCarouselData } from "../../../../utils/helper";

const emptyCarouselRow = {
  title: "",
  mediaLink: { mediaType: undefined, url: "", gifCreator: undefined },
  media: null,
};

const withGifDefaults = (rows) => {
  if (!Array.isArray(rows)) return rows;
  return rows.map((row) => {
    if (row?.mediaLink?.mediaType === "GIF") {
      return {
        ...row,
        mediaLink: {
          ...row.mediaLink,
          gifCreator: hydrateGifCreatorState(row.mediaLink),
        },
      };
    }
    return row;
  });
};

export default function CarouselComponent({
  dataLoader,
  form,
  _,
  loader: formDataLoader,
  tabHandleSaveClick,
}) {
  const { state } = useLocation();
  const tabKey = state;
  const { tabData } = useStore();
  const { micrositeId } = useParams();
  const tabConfigId = localStorage.getItem("selectedTabId");
  const whichTab = TAB_KEYS.corousel;
  const { deleteSingleItem, postLoader } = useTab();
  const { updateImage } = useConnectsuForm();
  
  const [loader, setLoader] = useState(formDataLoader || true);
  const [initialValues, setInitialValues] = useState([]);

  // new states for add/clear logic
  const [hasUnsavedItem, setHasUnsavedItem] = useState(false);
  const [sortedData, setSortedData] = useState([]);

  useEffect(() => {
    localStorage.setItem("tab", "CAROUSEL");
  }, []);

  useEffect(() => {
    refreshUnsavedState();
    setTimeout(() => {
      setLoader(false);
    }, 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabData[whichTab]]);

  const findValue = (i, key) => getFormFieldValue(form, whichTab, i, key);

  const SelectChangeHandler = (e, i) => {
    form.setFieldValue([whichTab, i, "mediaLink", "url"], "");
    form.setFieldValue([whichTab, i, "blob"], undefined);
    if (e === "GIF") {
      form.setFieldValue(
        [whichTab, i, "mediaLink", "gifCreator"],
        createEmptyGifState()
      );
    } else {
      form.setFieldValue([whichTab, i, "mediaLink", "gifCreator"], undefined);
    }
  };

  // ----------------- Handlers -----------------
  const handleAddMore = (e) => {
    const currentList = form.getFieldValue(whichTab) || [];
    const hasUnsavedItem = currentList.some((item) => !item?.id);
    if (hasUnsavedItem) {
      message.warning(
        "You must save the current item before you can add a new one."
      );
      return;
    }
    const tempList = [...currentList];
    tempList.splice(1, 0, { ...emptyCarouselRow });
    const newList = [...tempList];
    form.setFieldsValue({ [whichTab]: newList });
    setHasUnsavedItem(true);
    // window.scrollTo({ top: 0, behavior: "smooth" });
    const rect = e.currentTarget.getBoundingClientRect();
    const offset = 100;

    window.scrollTo({
      top: window.scrollY + rect.top - offset,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (tabData?.corousel?.length) {
      const sorted = withGifDefaults(sortCarouselData(tabData.corousel));
      setInitialValues(JSON.parse(JSON.stringify(sorted)));
      form.setFieldsValue({ [whichTab]: sorted });

      const sortedData = [...sorted];
      const item = sortedData.splice(1, 1)[0]; // Remove index 1
      sortedData.push(item); // Push to last
      setSortedData(sortedData);
    } else {
      setSortedData([])
    }
  }, [tabData?.corousel, form, whichTab]);

  const isDataChanged = () => {
    const current = form.getFieldValue(whichTab) || [];
    return JSON.stringify(current) !== JSON.stringify(initialValues);
  };

  const handleSave = async () => {
    try {
      await form.validateFields();

      const changed = isDataChanged();

      if (!changed) {
        message.info("No changes to save");
        return;
      }
      await form.submit();
      setTimeout(() => {
        setHasUnsavedItem(false);
      }, 500);
    } catch (err) {
      console.log("Validation failed:", err);
    }
  };

  const refreshUnsavedState = () => {
    const currentList = form.getFieldValue(whichTab) || [];
    const hasUnsavedItem = currentList.some((item) => !item?.id);
    setHasUnsavedItem(hasUnsavedItem);
  };

  const handleClear = () => {
    const currentList = form.getFieldValue(whichTab) || [];
    const updatedList = currentList.map((item) =>
      !item?.id
        ? { ...emptyCarouselRow }
        : item
    );
    form.setFieldsValue({ [whichTab]: updatedList });
  };

  const handleDelete = (i, name, remove) => {
    const item = form.getFieldValue(whichTab)?.[i];
    const id = item?.id;
    if (id) {
      deleteSingleItem(whichTab, id, form);
    } else {
      remove(name);
    }
    setTimeout(() => {
      refreshUnsavedState();
    }, 100);
  };

  // ---------------- Loader ----------------
  if (dataLoader || postLoader || loader) {
    return (
      <div className="data-loader">
        <Spin tip="Loading" size="large" />
      </div>
    );
  }
  
  return (
    <div className="newContantStyle">
      <Row className="rowBody" gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        {tabKey === whichTab ? (
          <>
            <Col
              className="gutter-row tabContent"
              xs={24}
              sm={24}
              md={tabData && tabData[whichTab] && tabData[whichTab]?.length > 0 ? 14 : 24}
              lg={tabData && tabData[whichTab] && tabData[whichTab]?.length > 0 ? 14 : 24}
              style={{ paddingRight: "25px" }}
            >
              <Form.List
                name={whichTab}
                initialValue={
                  tabData && tabData[whichTab] && tabData[whichTab]?.length > 0
                    ? withGifDefaults(sortCarouselData(tabData[whichTab]))
                    : [{ ...emptyCarouselRow }]
                }
              >
                {(fields, { remove, move }) => {
                  return (
                  <>
                    <div className="inputBody">
                      <label></label>
                      <div style={{ textAlign: "right" }}>
                        {hasUnsavedItem && (
                          <Button
                            className="cancelBtn"
                            type="button"
                            onClick={handleClear}
                          >
                            Clear
                          </Button>
                        )}

                        <Button className="greenBtn" onClick={(e) => handleAddMore(e)}>
                          Add <PlusOutlined />
                        </Button>
                      </div>
                    </div>

                    <DragAndDropList
                      fields={fields}
                      move={move}
                      form={form}
                      fieldName={whichTab}
                      disableFirst
                      onReorder={async (payload) => {
                        const response = await reArrangeOrder(
                          userServiceApi.reArrangeCardsData({ micrositeId, tabConfigId }),
                          payload
                        );
                        if (response?.length) {
                          const sorted = sortCarouselData(response)
                          const sortedData = [...sorted];
                          const item = sortedData.splice(1, 1)[0]; // Remove index 1
                          sortedData.push(item); // Push to last
                          setSortedData(sortedData);
                        }
                      }}
                      renderItem={({ key, name, ...restField }, i) => (
                        <Space
                          style={{
                            display: "block",
                            marginBottom: 8,
                            paddingRight: "40px",
                          }}
                          align="baseline"
                          className={`card-item businessCard ${i === 0 ? 'main-carousel-card' : ''}`}
                        >
                          <Col
                            className="gutter-row tabContent"
                            span={tabData && tabData[whichTab] && tabData[whichTab]?.length > 0 ? 24 : 16}
                            style={{ paddingRight: "25px" }}
                          >
                            {/* === RESTORED: Microsite carousel main card === */}
                            {i === 0 && (
                              <div
                                className="font-bold"
                                style={{
                                  textAlign: "center",
                                  paddingBottom: "10px",
                                }}
                              >
                                <strong>Main Video/Image</strong>
                              </div>
                            )}

                            {/* Title + Delete */}
                            <div className={`d-flex ${i === 0 ? 'main-carousel-text' : ''}`}>
                              <Form.Item
                                {...restField}
                                className="w-100"
                                label="Video/Image Title"
                                name={[name, "title"]}
                                labelCol={{ span: 5 }}
                                rules={[
                                  {
                                    required: true,
                                    message: "Image/Video Title is required",
                                  },
                                ]}
                              >
                                <Input
                                  placeholder="Image/Video Title"
                                  maxLength={40}
                                  showCount={{
                                    formatter: ({ count }) => `${count}/40`,
                                  }}

                                  onKeyDown={(e) => {
                                    const currentValue =
                                      form.getFieldValue([whichTab, name, "title"]) || "";

                                    if (
                                      e.key.length === 1 && // normal characters only
                                      currentValue.length >= 40
                                    ) {
                                      form.setFields([
                                        {
                                          name: [whichTab, name, "title"],
                                          errors: ["Title cannot exceed 40 characters"],
                                        },
                                      ]);
                                    }
                                  }}
                                  onBlur={() => {
                                    form.setFields([
                                      {
                                        name: [whichTab, name, "title"],
                                        errors: [],
                                      },
                                    ]);
                                  }}
                                />
                              </Form.Item>

                              <Form.Item>
                                <div className="remove-btn">
                                  {((tabData && tabData[whichTab] && tabData[whichTab]?.length) || 0) > 1 &&
                                    i !== 0 && (
                                      <DeleteOutlined
                                        onClick={() =>
                                          handleDelete(i, name, remove)
                                        }
                                      />
                                    )}
                                </div>
                              </Form.Item>
                            </div>

                            {/* Select: Video or Image */}
                            <div className={`d-flex ${i === 0 ? 'main-carousel-text' : ''}`}>
                              <Form.Item
                                {...restField}
                                className="w-100"
                                name={[name, "mediaLink", "mediaType"]}
                                label="Video or Image"
                                labelCol={{ span: 5 }}
                                rules={[
                                  {
                                    required: true,
                                    message: "Select any option",
                                  },
                                ]}
                              >
                                <Select
                                  onChange={(e) => SelectChangeHandler(e, i)}
                                  placeholder="Select Option"
                                >
                                  <Select.Option value="VIMEO_MEDIA">
                                    Add Vimeo Link
                                  </Select.Option>
                                  <Select.Option value="SOCIAL_MEDIA">
                                    Add Youtube Link
                                  </Select.Option>
                                  <Select.Option value="SYNTHESIA_MEDIA">
                                    Add Synthesia Link
                                  </Select.Option>
                                  <Select.Option value="IMAGE">
                                    Upload Image
                                  </Select.Option>
                                  <Select.Option value="GIF">
                                    Create GIF
                                  </Select.Option>
                                </Select>
                              </Form.Item>
                              <Form.Item>
                                <div className="remove-btn">
                                  <Tooltip title="Upload Video link or image with size lesser than 2.5MB. For best results, use images and videos with a 16:9 aspect ratio">
                                    <QuestionCircleOutlined />
                                  </Tooltip>
                                </div>
                              </Form.Item>
                            </div>

                            {/* Dynamic Fields by Type */}
                            <div className={`${i === 0 ? 'main-carousel-text' : ''}`}>
                              {findValue(i, "type") === "SOCIAL_MEDIA" ? (
                                <Form.Item
                                  {...restField}
                                  label="Youtube Link"
                                  labelCol={{ span: 5 }}
                                  name={[name, "mediaLink", "url"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Youtube link is required",
                                    },
                                  ]}
                                >
                                  <Input />
                                </Form.Item>
                              ) : findValue(i, "type") === "VIMEO_MEDIA" ? (
                                <Form.Item
                                  {...restField}
                                  label="Vimeo Link"
                                  labelCol={{ span: 5 }}
                                  name={[name, "mediaLink", "url"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Vimeo link is required",
                                    },
                                  ]}
                                >
                                  <Input />
                                </Form.Item>
                              ) : findValue(i, "type") === "IMAGE" ? (
                                <Form.Item
                                  {...restField}
                                  label={<ImageLabel label={"Image"} />}
                                  colon={false}
                                  name="media"
                                  className="experienceImg"
                                  labelCol={{ span: 5 }}
                                  rules={[
                                    {
                                      validator: (_, value) =>
                                        findValue(i, "blob") || findValue(i, "url")
                                          ? Promise.resolve()
                                          : Promise.reject(
                                            new Error("Image is required")
                                          ),
                                    },
                                  ]}
                                >
                                  {findValue(i, "blob") ? (
                                    <Avatar
                                      src={URL.createObjectURL(
                                        findValue(i, "blob")
                                      )}
                                      alt=""
                                    />
                                  ) : findValue(i, "url") ? (
                                    <Avatar src={findValue(i, "url")} alt="" />
                                  ) : (
                                    <Avatar src={UserIcon} />
                                  )}
                                  <label
                                    name="file"
                                    className="fileUploadBtn greenBtn"
                                  >
                                    Upload Image
                                    <input
                                      accept="image/*" 
                                      type="file"
                                      onChange={(e) =>
                                        updateImage(e, i, form, whichTab)
                                      }
                                    />
                                  </label>
                                  <Tooltip
                                    className="question"
                                    title={"Recommended image dimensions: 1280x720 pixels"}
                                  >
                                    <QuestionCircleOutlined />
                                  </Tooltip>
                                </Form.Item>
                              ) : findValue(i, "type") === "GIF" ? (
                                <>
                                  <Form.Item
                                    {...restField}
                                    label="Create GIF"
                                    colon={false}
                                    name={[name, "mediaLink", "gifCreator"]}
                                    className="gif-creator-field"
                                    labelCol={{ span: 5 }}
                                    initialValue={createEmptyGifState()}
                                  >
                                    <GifCreator />
                                  </Form.Item>
                                  <Form.Item shouldUpdate noStyle>
                                    {() => {
                                      const row =
                                        form.getFieldValue(whichTab)?.[i];
                                      if (!row) return null;
                                      const gifState =
                                        row.mediaLink?.gifCreator || {};
                                      const gifUrl =
                                        gifState.previewUrl ||
                                        row.mediaLink?.url;
                                      return (
                                        <GifResultPreview
                                          url={gifUrl}
                                          fileName={gifState.fileName}
                                          loading={!!gifState.generating}
                                          showEdit={
                                            !!gifUrl &&
                                            gifState.isEditing === false
                                          }
                                          onEdit={() => {
                                            const current = form.getFieldValue([
                                              whichTab,
                                              i,
                                              "mediaLink",
                                              "gifCreator",
                                            ]);
                                            form.setFieldValue(
                                              [
                                                whichTab,
                                                i,
                                                "mediaLink",
                                                "gifCreator",
                                              ],
                                              {
                                                ...createEmptyGifState(),
                                                ...current,
                                                isEditing: true,
                                              }
                                            );
                                          }}
                                        />
                                      );
                                    }}
                                  </Form.Item>
                                </>
                              ) : findValue(i, "type") === "SYNTHESIA_MEDIA" ? (
                                <Form.Item
                                  {...restField}
                                  label="Synthesia Link"
                                  labelCol={{ span: 5 }}
                                  name={[name, "mediaLink", "url"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Synthesia link is required",
                                    },
                                  ]}
                                >
                                  <Input />
                                </Form.Item>
                              ) : null}
                            </div>

                            {/* Save Button */}
                            <Form.Item
                              className="w-100"
                              style={{ display: "flex", justifyContent: "end" }}
                            >
                              <Button
                                className="saveBtn greenBtn save_align"
                                onClick={() => {
                                  handleSave();
                                }}
                                disabled={!isDataChanged()}
                              >
                                Save
                              </Button>
                            </Form.Item>
                          </Col>
                        </Space>
                      )}
                    />
                  </>
                )}
              }
              </Form.List>
            </Col>

            {/* Right-side slider */}
            <Col
              className="gutter-row tabContent"
              xs={24}
              sm={24}
              md={10}
              lg={10}
            >
              <ImageSlider
                data={
                  sortedData?.length ? sortedData : [""]
                }
              />
            </Col>
          </>
        ) : null}
      </Row>
    </div>
  );
}
